import {
  AdditiveBlending,
  Color,
  CylinderGeometry,
  DoubleSide,
  Mesh,
  MeshBasicNodeMaterial,
  type Scene,
} from 'three/webgpu';
import { requestLatePrecompile } from '../render/renderer.js';
import { uv } from 'three/tsl';
import { EntityKind, RARITY_COLORS, type EntitySnap } from '@stillsun/shared';

/**
 * Rarity beams over gear drops.
 *
 * The server reuses a loot bag's otherwise-unused `EntitySnap.state` byte as `rarity + 1`
 * for an M6 gear drop; `0` is an ordinary materials bag and gets nothing, exactly as
 * before. So this is a read-only passenger on the snapshot stream: `game/entities.ts` still
 * owns the sack itself, and this module only adds the pillar of light above it.
 *
 * Kept deliberately cheap — one shared geometry, one material per rarity (five, ever),
 * additive, no shadows, no depth write. The beams are what the eye catches across a yard,
 * so the colour is the whole payload; the slow swirl below is there to stop five static
 * cylinders reading as level geometry.
 *
 * Bags are pruned on staleness rather than on absence from a single snapshot, mirroring
 * `EntityManager`: a dropped packet must not blink every beam in the raid.
 */

/** No snapshot for this long and the bag is gone (looted, or the five-minute despawn). */
const STALE_MS = 3000;

/**
 * Sized and lit against the *worst* case, which on the Spine is the dayside: a blown-out white
 * horizon that an additive beam cannot add to. Measured in `tools/scripts/gear-shots.mjs` — at
 * 3.2 m and 1x intensity only the Epic purple read at all. Taller than a Waker so it clears the
 * dune line, and over-driven past 1.0 per channel so the bloom pass catches it and the colour
 * survives the sky. At night this is the brightest thing in the yard, which is the point.
 */
const BEAM_HEIGHT = 5.5;
const BEAM_RADIUS_BOTTOM = 0.42;
const BEAM_RADIUS_TOP = 0.16;
/** Linear-space over-drive, so the beam is an emitter rather than a tinted pane of glass. */
const BEAM_INTENSITY = 2.6;
/** Clear of the sack (which sits at y ≈ 0.2, scaled to 0.72 of a 0.45 m box). */
const BEAM_BASE_Y = 0.05;

let geometry: CylinderGeometry | null = null;
function beamGeometry(): CylinderGeometry {
  if (!geometry) {
    geometry = new CylinderGeometry(BEAM_RADIUS_TOP, BEAM_RADIUS_BOTTOM, BEAM_HEIGHT, 10, 1, true);
    // Origin at the base, so a bag's world position can be used unmodified and the pulse
    // below scales the beam upward instead of sinking it into the floor.
    geometry.translate(0, BEAM_HEIGHT / 2, 0);
  }
  return geometry;
}

/** One material per rarity, built on first sight of that rarity. */
const materials = new Map<number, MeshBasicNodeMaterial>();
function beamMaterial(rarity: number): MeshBasicNodeMaterial {
  const have = materials.get(rarity);
  if (have) return have;
  const hex = RARITY_COLORS[rarity] ?? RARITY_COLORS[0];
  const m = new MeshBasicNodeMaterial({
    color: new Color(hex).multiplyScalar(BEAM_INTENSITY),
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
  });
  // A cylinder's v runs 0 at the base to 1 at the cap; fading it out kills the hard rim
  // that otherwise reads as a piece of pipe standing in the yard. The base stays at full
  // strength so there is a bright root the eye can find across a yard.
  m.opacityNode = uv().y.oneMinus().pow(1.35);
  materials.set(rarity, m);
  return m;
}

interface Beam {
  readonly mesh: Mesh;
  rarity: number;
  /** `performance.now()` of the last snapshot that carried this bag. */
  seen: number;
  /** Per-bag phase, so a field of drops does not breathe in unison. */
  readonly phase: number;
}

export class LootBeams {
  private readonly scene: Scene;
  private readonly beams = new Map<number, Beam>();
  private clock = 0;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /** Adds, moves and recolours beams for every gear drop in this snapshot. */
  onSnapshot(entities: readonly EntitySnap[]): void {
    const now = performance.now();
    for (const s of entities) {
      if (s.kind !== EntityKind.LootBag) continue;
      // 0 = an ordinary materials bag: no beam, unchanged behaviour.
      if (s.state <= 0) continue;
      const rarity = Math.min(RARITY_COLORS.length - 1, Math.max(0, s.state - 1));
      let beam = this.beams.get(s.id);
      if (!beam) {
        const mesh = new Mesh(beamGeometry(), beamMaterial(rarity));
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        mesh.frustumCulled = true;
        mesh.renderOrder = 8;
        beam = { mesh, rarity, seen: now, phase: (s.id % 32) * 0.37 };
        this.beams.set(s.id, beam);
        this.scene.add(mesh);
        requestLatePrecompile('lootbeam');
      } else if (beam.rarity !== rarity) {
        beam.rarity = rarity;
        beam.mesh.material = beamMaterial(rarity);
      }
      beam.seen = now;
      beam.mesh.position.set(s.pos.x, s.pos.y + BEAM_BASE_Y, s.pos.z);
    }
  }

  /** A slow swirl, and the staleness sweep that retires looted or despawned bags. */
  update(dt: number): void {
    this.clock += dt;
    const now = performance.now();
    for (const [id, beam] of this.beams) {
      if (now - beam.seen > STALE_MS) {
        this.scene.remove(beam.mesh);
        this.beams.delete(id);
        continue;
      }
      const t = this.clock * 1.5 + beam.phase;
      const wobble = Math.sin(t) * 0.11;
      // Squashed out of round and turned: the only way a smooth untextured cylinder can
      // show that it is moving at all, and it costs three trig calls a bag.
      beam.mesh.scale.set(1 + wobble, 1 + Math.sin(t * 0.7) * 0.06, 1 - wobble);
      beam.mesh.rotation.y += dt * 0.8;
    }
  }

  /**
   * What the scene is actually holding, for `tools/scripts/gear-shots.mjs` — a probe should assert
   * on state, not squint at a screenshot for a coloured pillar. Cheap and side-effect free.
   */
  debugState(): { count: number; beams: { id: number; rarity: number; colour: string }[] } {
    const beams = [...this.beams.entries()].map(([id, b]) => ({
      id,
      rarity: b.rarity,
      colour: `#${(RARITY_COLORS[b.rarity] ?? 0).toString(16).padStart(6, '0')}`,
    }));
    return { count: beams.length, beams };
  }

  /** Drops every beam. The shared geometry and materials survive for the next raid. */
  clear(): void {
    for (const beam of this.beams.values()) this.scene.remove(beam.mesh);
    this.beams.clear();
  }

  /** Teardown: also releases the module-level geometry and the rarity materials. */
  dispose(): void {
    this.clear();
    geometry?.dispose();
    geometry = null;
    for (const m of materials.values()) m.dispose();
    materials.clear();
  }
}
